import { useEffect } from "react";

const CSS_VAR_DEADBAND_PX = 2;
const STICKY_UNPIN_GAP_PX = 24;

const DEFAULT_OPTIONS = {
  pinClass: "products_page_sticky_zone_is_stuck",
  headerOffsetVar: "--products-page-header-offset",
  sidebarTopVar: "--products-page-sidebar-sticky-top",
};

export const useProductPageStickyLayout = (
  isTabletOrMobile,
  stickyZoneRef,
  filterBarRef,
  options = {}
) => {
  const { pinClass, headerOffsetVar, sidebarTopVar } = { ...DEFAULT_OPTIONS, ...options };

  useEffect(() => {
    if (isTabletOrMobile) {
      stickyZoneRef.current?.classList.remove(pinClass);
      document.documentElement.style.removeProperty(headerOffsetVar);
      document.documentElement.style.removeProperty(sidebarTopVar);
      return undefined;
    }

    let rafId = null;
    let isPinned = false;
    let lastHeaderOffset = -1;
    let lastSidebarTop = -1;

    const setVarIfChanged = (name, nextValue) => {
      const prevValue = name === headerOffsetVar ? lastHeaderOffset : lastSidebarTop;

      if (prevValue >= 0 && Math.abs(nextValue - prevValue) < CSS_VAR_DEADBAND_PX) {
        return;
      }

      document.documentElement.style.setProperty(name, `${nextValue}px`);

      if (name === headerOffsetVar) {
        lastHeaderOffset = nextValue;
      } else {
        lastSidebarTop = nextValue;
      }
    };

    const updateLayout = () => {
      const headerEl = document.querySelector(".header_container > div");
      const zoneEl = stickyZoneRef.current;
      const filterEl = filterBarRef.current;

      if (!headerEl || !zoneEl || !filterEl) {
        return;
      }

      const headerBottom = Math.max(0, Math.round(headerEl.getBoundingClientRect().bottom));
      const zoneTop = zoneEl.getBoundingClientRect().top;
      const filterBottom = Math.round(filterEl.getBoundingClientRect().bottom);

      setVarIfChanged(headerOffsetVar, headerBottom);
      setVarIfChanged(sidebarTopVar, filterBottom);

      const shouldPin = zoneTop <= headerBottom + 2;

      if (!isPinned && shouldPin) {
        isPinned = true;
        zoneEl.classList.add(pinClass);
      } else if (isPinned && zoneTop > headerBottom + STICKY_UNPIN_GAP_PX) {
        isPinned = false;
        zoneEl.classList.remove(pinClass);
      }

      if (isPinned) {
        document.documentElement.style.setProperty(headerOffsetVar, `${headerBottom}px`);
        lastHeaderOffset = headerBottom;
      }
    };

    const scheduleUpdate = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(updateLayout);
    };

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    const headerEl = document.querySelector(".header_container > div");
    const zoneEl = stickyZoneRef.current;
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleUpdate) : null;

    if (headerEl && resizeObserver) {
      resizeObserver.observe(headerEl);
    }

    scheduleUpdate();

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver?.disconnect();
      zoneEl?.classList.remove(pinClass);
      document.documentElement.style.removeProperty(headerOffsetVar);
      document.documentElement.style.removeProperty(sidebarTopVar);
    };
  }, [filterBarRef, headerOffsetVar, isTabletOrMobile, pinClass, sidebarTopVar, stickyZoneRef]);
};
